import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useVirtualWorld from "../../hooks/useVirtualWorld";
import vw from "../../api/virtualWorld.api";
import GuideUrgencyTimer from "./GuideUrgencyTimer";

const pathMatches = (pathname, to) => {
  if (!to) return false;
  if (pathname === to) return true;
  if (to !== "/manager" && pathname.startsWith(`${to}/`)) return true;
  return false;
};

/**
 * Persistente Onboarding-Leiste auf Unterseiten.
 * Auf der Club-Home entfällt sie — dort gibt es nur den einen Fokus-Schritt.
 */
const GuideBanner = () => {
  const { userId, manager, setManager, refresh } = useVirtualWorld();
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const guide = manager?.guide || manager?.progression?.manager?.guide;
  const step = guide?.current;
  const onHome = location.pathname === "/manager" || location.pathname === "/manager/";
  if (!manager?.guided || !step || onHome) return null;

  const atTarget = pathMatches(location.pathname, step.to);

  const ackAndContinue = async () => {
    if (!step.needsAck) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await vw.acknowledgeGuide(userId, step.needsAck);
      if (res?.data) setManager(res.data);
      else await refresh();
      const next =
        res?.data?.guide?.current ||
        res?.data?.progression?.manager?.guide?.current ||
        null;
      if (next?.to) navigate(next.to);
    } catch (err) {
      setMsg(err?.data?.message || err?.message || "Konnte nicht bestätigt werden.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-amber-200/80 bg-amber-50/90 dark:bg-gray-800 p-4 mb-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-amber-700/80 dark:text-amber-300 mb-1">
            Schritt {step.index}/{step.total}
          </div>
          <h2 className="text-xl font-black mb-1">{step.title}</h2>
          <p className="text-sm opacity-75 mb-3">{step.hint}</p>
          {msg ? <p className="text-red-600 mb-2 text-sm">{msg}</p> : null}
          <div className="flex flex-wrap gap-2">
            {step.needsAck && atTarget ? (
              <button
                type="button"
                disabled={busy}
                onClick={ackAndContinue}
                className="px-4 py-2 rounded-xl bg-amber-700 text-amber-50 font-semibold text-sm"
              >
                Verstanden — weiter
              </button>
            ) : (
              <Link
                to={step.to || "/manager"}
                className="inline-flex px-4 py-2 rounded-xl bg-amber-700 text-amber-50 font-semibold text-sm"
              >
                {step.cta || "Weiter"}
              </Link>
            )}
            {step.needsAck && !atTarget ? (
              <button
                type="button"
                disabled={busy}
                onClick={ackAndContinue}
                className="px-4 py-2 rounded-xl bg-white dark:bg-gray-700 font-semibold border text-sm"
              >
                Schon klar
              </button>
            ) : null}
          </div>
        </div>
        <div className="shrink-0 w-full sm:w-40">
          <GuideUrgencyTimer
            stepKey={step.key}
            urgencySec={step.urgencySec || 90}
            label="Zeit"
            compact
            calm
          />
        </div>
      </div>
    </div>
  );
};

export default GuideBanner;
